import { ChangeEvent, KeyboardEvent, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Typography from '@mui/material/Typography'
import { MagnifyingGlass as MagnifyingGlassIcon } from '@phosphor-icons/react/dist/ssr/MagnifyingGlass'
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X'

import type { Operator } from '@/types/operator'
import { useWorkspaces } from '@/hooks/use-workspaces'
import { UserAvatar } from '@/components/core/user-avatar'

export interface WorkspacesPopoverProps {
	selectedOperatorId?: number
	anchorEl: null | Element
	onChange?: (operatorId: number) => void
	onClose?: () => void
	open?: boolean
}

export function WorkspacesPopover({
	selectedOperatorId,
	anchorEl,
	onChange,
	onClose,
	open = false,
}: WorkspacesPopoverProps) {
	const {
		operators,
		operatorSearch,
		setOperatorSearch,
		hasMoreOperators,
		loadMoreOperators,
		isOperatorsLoading,
	} = useWorkspaces()

	return (
		<Menu
			anchorEl={anchorEl}
			anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
			onClose={onClose}
			open={open}
			slotProps={{
				paper: { sx: { width: '250px' } },
			}}
			MenuListProps={{
				sx: { paddingRight: '8px !important', maxHeight: 420 },
			}}
			transformOrigin={{ horizontal: 'right', vertical: 'top' }}
			sx={{ mt: 0.5 }}
			disableAutoFocusItem
		>
			<Box sx={searchFilterBoxProps}>
				<SearchFilter searchFilter={operatorSearch} setSearchFilter={setOperatorSearch} />
			</Box>
			{operators.map((operator: Operator) => (
				<MenuItem
					key={operator.id}
					onClick={() => {
						onChange?.(operator.id)
					}}
					selected={operator.id === selectedOperatorId}
				>
					<ListItemAvatar>
						<UserAvatar name={operator.name} src={operator.photo} sx={{ '--Avatar-size': '32px' }} variant="rounded" />
					</ListItemAvatar>
					<Typography variant="body1" noWrap maxWidth={170}>
						{operator.name}
					</Typography>
				</MenuItem>
			))}
			{!operators.length && !isOperatorsLoading ? (
				<MenuItem disabled>
					<Typography color="text.secondary" variant="body2">
						No operators found
					</Typography>
				</MenuItem>
			) : null}
			{hasMoreOperators ? (
				<Box sx={{ px: 1, pb: 1 }}>
					<Button
						fullWidth
						color="secondary"
						size="small"
						onClick={loadMoreOperators}
						disabled={isOperatorsLoading}
						startIcon={isOperatorsLoading ? <CircularProgress size={14} /> : null}
					>
						Load more
					</Button>
				</Box>
			) : null}
		</Menu>
	)
}

function SearchFilter({ searchFilter, setSearchFilter }) {
	const handleSearchFilterChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setSearchFilter(event.target.value)
		},
		[setSearchFilter]
	)

	const handleClearSearchFilter = useCallback(() => {
		setSearchFilter('')
	}, [setSearchFilter])

	const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
		event.stopPropagation()
		if (event.key === 'Enter') {
			event.preventDefault()
		}
	}, [])

	return (
		<OutlinedInput
			onChange={handleSearchFilterChange}
			onKeyDown={handleKeyDown}
			placeholder="Search operators"
			fullWidth
			startAdornment={
				<InputAdornment position="start">
					<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
				</InputAdornment>
			}
			endAdornment={
				searchFilter ? (
					<InputAdornment position="end">
						<IconButton aria-label="Clear search" edge="end" onClick={handleClearSearchFilter} size="small">
							<XIcon />
						</IconButton>
					</InputAdornment>
				) : null
			}
			value={searchFilter}
			sx={{ mb: 1 }}
		/>
	)
}

const searchFilterBoxProps = {
	position: 'sticky',
	top: 0,
	pt: 1,
	mt: -1,
	zIndex: 1,
	backgroundColor: 'background.paper',
}
